/* ─────────────────────────────────────────────────────────────────────────────
 * EvidenceStrip — the context frames around a decision.
 *
 * Both review pages used to show ONE image per record, and that image is not
 * guaranteed to be the frame the decision was made on: the alert photo keeps the
 * first qualifying frame of a visit, so it can be seconds — measured as much as
 * a thousand — older than the moment that actually mattered. A reviewer then marks
 * "correct block" or "false alert" from a picture unrelated to the decision, and
 * those verdicts are what every detection threshold is derived from. Bad frame →
 * bad label → bad threshold, with nothing in the loop to catch it.
 *
 * Showing the sequence removes the argument. The frames already existed on disk for
 * alerts (the 8-frame sidecar strip, written next to the clip and read only by face
 * enrolment); blocked records now save a short strip of their own.
 *
 * Lazy: asks for the count first and renders nothing when there is no strip, so
 * older records and cameras with the diagnostic switch off look exactly as before.
 * ──────────────────────────────────────────────────────────────────────────── */
import { useState, useEffect } from 'react';
import { apiFetch, apiImageUrl } from '../utils/streamApi';

export default function EvidenceStrip({ countPath, framePath, onOpen, label = 'Context' }) {
  const [urls, setUrls] = useState([]);
  const [ages, setAges] = useState([]);

  useEffect(() => {
    let alive = true;
    setUrls([]); setAges([]);
    apiFetch(countPath)
      .then(r => (r.ok ? r.json() : { count: 0 }))
      .then(async d => {
        const n = Math.max(0, Math.min(8, Number(d?.count) || 0));
        if (!alive || !n) return;
        const list = await Promise.all(
          Array.from({ length: n }, (_, i) => apiImageUrl(framePath(i)))
        );
        if (alive) { setUrls(list); setAges(Array.isArray(d?.ages) ? d.ages : []); }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [countPath, framePath]);

  if (!urls.length) return null;

  // Span tells you at a glance whether identical-looking thumbnails mean "stood
  // still for 20s" or "these are 2s apart" — without it a repeated frame reads as
  // a fault in the strip rather than as information about the person.
  const nums = ages.filter(a => typeof a === 'number');
  const span = nums.length > 1 ? Math.abs(Math.max(...nums) - Math.min(...nums)) : null;

  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 3 }}>
        {label} — {urls.length === 1
          ? 'earlier frame'
          : `${urls.length} frames, oldest first`}
        {span != null && <> · spanning {span.toFixed(0)}s</>}
      </div>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {urls.map((u, i) => {
          const a = ages[i];
          const tag = typeof a === 'number' ? (a >= 0 ? `−${a.toFixed(0)}s` : `+${Math.abs(a).toFixed(0)}s`) : null;
          return (
            <figure key={i} style={{ margin: 0, textAlign: 'center' }}>
              <img
                src={u}
                alt={`frame ${i + 1}`}
                title={tag ? `${tag} before this record` : `Frame ${i + 1} of ${urls.length}`}
                onClick={() => onOpen && onOpen(u)}
                style={{
                  width: 96, height: 54, objectFit: 'cover',
                  borderRadius: 4, border: '1px solid var(--border)',
                  cursor: onOpen ? 'zoom-in' : 'default', display: 'block',
                }}
                onError={e => { e.currentTarget.parentElement.style.display = 'none'; }}
              />
              <figcaption style={{ fontSize: 10, color: 'var(--text-muted)', marginTop: 1 }}>
                {tag || `#${i + 1}`}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
}
